/**
 * script_recently-list.js
 *
 * 最近閲覧商品の保存と、画像・タイトル・価格・リンクの簡易カード描画を担当する。
 * script_cart-actions.js とは完全に独立しており、カート/お気に入り連携は持たない。
 */
(function () {
  // 同じスクリプトが複数回読み込まれても一度だけ初期化する。
  if (window.__roiRecentlyListInitialized) return;
  window.__roiRecentlyListInitialized = true;

  /* ============================================================
     定数
     ============================================================ */

  // 最近閲覧データを保存する localStorage のキー。
  const RECENTLY_VIEWED_KEY = 'roi:recently-viewed:v1';

  // 保存件数の上限。
  const MAX_ITEMS = 10;

  // 商品 JSON の同時取得数を制限する。
  const FETCH_CONCURRENCY = 4;

  /* ============================================================
     キャッシュ
     ============================================================ */

  // 同じ商品を何度も fetch しないためのキャッシュ。
  const productCache = new Map();

  /* ============================================================
     バリデーション
     ============================================================ */

  // 商品IDは数字文字列だけを許可する。
  function isValidId(val) {
    return typeof val === 'string' && /^\d+$/.test(val.trim());
  }

  // handle は Shopify の形式に合わせて制限する。
  function isValidHandle(val) {
    return typeof val === 'string' && /^[a-z0-9][a-z0-9-]*$/.test(val.trim());
  }

  // 商品URLは /products/{handle} の相対パスに完全一致するものだけを許可する。
  // buildProductCard() で再構築した URL の最終防御チェック専用。
  function isValidProductUrl(val) {
    return typeof val === 'string' && /^\/products\/[a-z0-9][a-z0-9-]*$/.test(val);
  }

  /* ============================================================
     localStorage
     ============================================================ */

  // 保存済みの最近閲覧データを安全に読み出す。
  // 各フィールドを必ず文字列・数値へ正規化して返すことで、
  // 過去に数値型で保存された id などが原因で比較が失敗しないようにする。
  // 旧データに url フィールドが含まれていても無視して継続利用できる。
  function getRecentlyViewed() {
    try {
      const raw = localStorage.getItem(RECENTLY_VIEWED_KEY);
      if (!raw) return [];

      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];

      return parsed
        .filter((entry) => {
          if (!entry || typeof entry !== 'object') return false;

          const id = String(entry.id ?? '');
          const handle = String(entry.handle ?? '');
          const viewedAt = Number(entry.viewedAt ?? 0);

          // id と handle だけで有効性を判断する。url は検証しない。
          if (!isValidId(id) || !isValidHandle(handle)) {
            return false;
          }

          return Number.isFinite(viewedAt) && viewedAt > 0;
        })
        // バリデーション済みの id / handle / viewedAt だけを使って型を揃えた新オブジェクトを作る。
        // url は保存対象から外すため、ここで除去する。
        .map((entry) => ({
          id: String(entry.id),
          handle: String(entry.handle),
          viewedAt: Number(entry.viewedAt),
        }))
        .slice(0, MAX_ITEMS);
    } catch {
      return [];
    }
  }

  // 最近閲覧データを安全に保存する。
  function saveRecentlyViewed(entries) {
    try {
      localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(entries));
    } catch {
      // 保存できない環境では何もしない。
    }
  }

  /* ============================================================
     商品ページの現在商品
     ============================================================ */

  // 商品ページの専用フック属性から現在の商品情報を取得する。
  // data-recent-product-entry を持つ要素を取得することで、
  // 見た目用クラスの変更に影響されず安定して動作する。
  function getCurrentProductEntry() {
    const card = document.querySelector('[data-recent-product-entry]');
    if (!card) return null;

    const id = String(card.dataset.productId ?? '');
    const handle = String(card.dataset.productHandle ?? '');

    if (!isValidId(id) || !isValidHandle(handle)) {
      return null;
    }

    return { id, handle, viewedAt: Date.now() };
  }

  // 商品ページを開いた時に最近閲覧へ追加する。
  function saveCurrentProduct() {
    const currentProduct = getCurrentProductEntry();
    if (!currentProduct) return;

    // id と handle の両方で重複チェックし、型揺れによる重複残りを防ぐ。
    const entries = getRecentlyViewed().filter(
      (entry) =>
        String(entry.id) !== currentProduct.id &&
        entry.handle !== currentProduct.handle
    );
    entries.unshift(currentProduct);
    saveRecentlyViewed(entries.slice(0, MAX_ITEMS));
  }

  // 現在表示中の商品 handle を返す。
  function getCurrentProductHandle() {
    const currentProduct = getCurrentProductEntry();
    return currentProduct ? currentProduct.handle : null;
  }

  /* ============================================================
     ユーティリティ
     ============================================================ */

  // 非同期処理を指定数ずつ並列実行する。
  async function runWithConcurrency(tasks, concurrency) {
    const results = new Array(tasks.length);
    let nextIndex = 0;

    async function runNext() {
      if (nextIndex >= tasks.length) return;
      const index = nextIndex++;
      results[index] = await tasks[index]();
      await runNext();
    }

    const workers = Array.from(
      { length: Math.min(concurrency, tasks.length) },
      runNext
    );

    await Promise.all(workers);
    return results;
  }

  /* ============================================================
     商品取得
     ============================================================ */

  // handle から商品JSONを取得する。
  async function fetchProductByHandle(handle) {
    if (productCache.has(handle)) return productCache.get(handle);

    try {
      const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return null;

      const product = await res.json();
      if (
        !product ||
        typeof product.id !== 'number' ||
        typeof product.title !== 'string' ||
        typeof product.handle !== 'string'
      ) {
        return null;
      }

      productCache.set(handle, product);
      return product;
    } catch {
      return null;
    }
  }

  /* ============================================================
     カード生成
     ============================================================ */

  // 商品JSONから画像・タイトル・価格・リンクの簡易カードを組み立てる。
  function buildProductCard(product) {
    const productUrl = `/products/${product.handle}`;

    if (!isValidHandle(product.handle) || !isValidProductUrl(productUrl)) {
      return null;
    }

    // <li class="recently-list__item">
    const li = document.createElement('li');
    li.className = 'recently-list__item';

    // 商品ページへのリンク
    const link = document.createElement('a');
    link.href = productUrl;
    link.className = 'recently-list__card';

    if (product.featured_image) {
      const img = document.createElement('img');
      img.className = 'recently-list__image';
      img.src = product.featured_image;
      img.alt = product.title;
      img.width = 300;
      img.height = 300;
      img.loading = 'lazy';
      link.appendChild(img);
    }

    // 商品タイトル
    const titleEl = document.createElement('p');
    titleEl.className = 'recently-list__title';
    titleEl.textContent = product.title;
    link.appendChild(titleEl);

    const variants = Array.isArray(product.variants) ? product.variants : [];
    const variant = variants.find((entry) => entry?.available) || variants[0];
    const price = variant?.price;
    if (Number.isFinite(price) && price >= 0) {
      const priceEl = document.createElement('p');
      priceEl.className = 'recently-list__price';
      priceEl.textContent = new Intl.NumberFormat('ja-JP', { maximumFractionDigits: 0 }).format(price / 100);
      const unitEl = document.createElement('small');
      unitEl.className = 'recently-list__price-unit';
      unitEl.textContent = '円（税込）';
      priceEl.appendChild(unitEl);
      link.appendChild(priceEl);
    }

    li.appendChild(link);
    return li;
  }

  /* ============================================================
     セクション描画
     ============================================================ */

  // 空状態に応じてセクション自体の表示を切り替える。
  function setEmptyState(section, hasItems) {
    const hideWhenEmpty = section.dataset.hideWhenEmpty === 'true';
    section.hidden = hideWhenEmpty && !hasItems;
  }

  // セクションごとに現在の描画トークンを管理する。
  // 古い描画が fetch 完了後に append するのを防ぐために使う。
  const renderTokens = new WeakMap();

  // 単一の recently-list セクションを描画する。
  async function renderRecentlyList(section) {
    const list = section.querySelector('.recently-list__list');
    if (!list) return;

    // このセクションの描画トークンを更新し、自分が最新かどうか確認できるようにする。
    // fetch 完了後にトークンが変わっていれば、古い描画として結果を破棄する。
    const token = Symbol();
    renderTokens.set(section, token);

    const loadingMsg = section.querySelector('.recently-list__loading');
    const emptyMsg = section.querySelector('.recently-list__empty');
    const limit = Math.min(Number(section.dataset.limit || MAX_ITEMS), MAX_ITEMS);
    const currentHandle = getCurrentProductHandle();

    list.replaceChildren();
    if (loadingMsg) loadingMsg.hidden = false;
    if (emptyMsg) emptyMsg.hidden = true;

    // 現在表示中の商品を除いた上で、handle の重複も除去してから表示件数に絞る。
    // localStorage に壊れたデータが残っていても UI 上で2件出ないようにする保険。
    const seenHandles = new Set();
    const entries = getRecentlyViewed()
      .filter((entry) => {
        if (entry.handle === currentHandle) return false;
        if (seenHandles.has(entry.handle)) return false;
        seenHandles.add(entry.handle);
        return true;
      })
      .slice(0, limit);

    if (entries.length === 0) {
      if (loadingMsg) loadingMsg.hidden = true;
      if (emptyMsg) emptyMsg.hidden = false;
      setEmptyState(section, false);
      return;
    }

    const tasks = entries.map((entry) => () => fetchProductByHandle(entry.handle));
    const products = await runWithConcurrency(tasks, FETCH_CONCURRENCY);

    // fetch 完了後にトークンが変わっていれば、より新しい描画が起動しているので中断する。
    if (renderTokens.get(section) !== token) return;

    products.forEach((product) => {
      if (!product) return;
      const card = buildProductCard(product);
      if (card) list.appendChild(card);
    });

    if (loadingMsg) loadingMsg.hidden = true;

    const hasItems = list.children.length > 0;
    if (!hasItems && emptyMsg) emptyMsg.hidden = false;
    setEmptyState(section, hasItems);
  }

  // ページ内の recently-list セクションをまとめて描画する。
  function renderAllSections() {
    document.querySelectorAll('.recently-list').forEach((section) => {
      renderRecentlyList(section);
    });
  }

  /* ============================================================
     イベント
     ============================================================ */

  // 別タブで最近閲覧が変わったら再描画する。
  window.addEventListener('storage', (e) => {
    if (e.key === RECENTLY_VIEWED_KEY) {
      renderAllSections();
    }
  });

  // BFCache から復帰した時だけ保存と再描画を行う。
  // e.persisted を確認することで通常の初回表示時に init() と二重発火するのを防ぐ。
  window.addEventListener('pageshow', (e) => {
    if (!e.persisted) return;
    saveCurrentProduct();
    renderAllSections();
  });

  // テーマエディタで recently-list セクションが再読込された時だけ描画する。
  document.addEventListener('shopify:section:load', (e) => {
    const target = e.target;
    if (!(target instanceof Element)) return;

    const section = target.matches('.recently-list')
      ? target
      : target.querySelector('.recently-list');

    if (section) {
      renderRecentlyList(section);
    }
  });

  /* ============================================================
     初期化
     ============================================================ */

  // 初回ロード時に商品保存とセクション描画を行う。
  function init() {
    saveCurrentProduct();
    renderAllSections();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
